import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3Force from 'd3-force';
import type { SearchIndexEntry } from '@claude-code-insights/shared';
import { fetchSearchIndex } from '../lib/api.js';

interface GraphNode extends d3Force.SimulationNodeDatum {
  id: string;
  title: string;
  category: string;
  relevanceScore: number;
  tags: string[];
  el?: SVGCircleElement;
}

interface GraphLink extends d3Force.SimulationLinkDatum<GraphNode> {
  weight: number;
  el?: SVGLineElement;
}

const CATEGORY_COLORS: Record<string, string> = {
  'mcp-server': '#4caf50',
  'hook': '#8888dd',
  'workflow': '#ff9800',
  'claude-md-pattern': '#b088cc',
  'prompting-technique': '#ccaa00',
  'skill': '#7f6df2',
  'tool-usage': '#4dd0e1',
  'configuration': '#ff7043',
  'general-tip': '#90a4ae',
  'model-comparison': '#f48fb1',
  'api-integration': '#80cbc4',
  'agent-framework': '#ce93d8',
  'rag-pattern': '#a5d6a7',
};

function getCategoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? '#666';
}

function nodeRadius(n: GraphNode): number {
  return 6 + n.relevanceScore * 1.2;
}

function buildGraph(entries: SearchIndexEntry[]): { nodes: GraphNode[]; links: GraphLink[] } {
  const nodes: GraphNode[] = entries.map((e) => ({
    id: e.id,
    title: e.title,
    category: e.category,
    relevanceScore: e.relevanceScore,
    tags: e.tags ?? [],
  }));
  const links: GraphLink[] = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const shared = nodes[i].tags.filter((t) => nodes[j].tags.includes(t)).length;
      if (shared > 0) links.push({ source: nodes[i].id, target: nodes[j].id, weight: shared });
    }
  }
  return { nodes, links };
}

export default function GraphView() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [allEntries, setAllEntries] = useState<SearchIndexEntry[]>([]);
  const [filteredCategory, setFilteredCategory] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ node: GraphNode; x: number; y: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchIndex()
      .then((entries) => { setAllEntries(entries); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!svgRef.current || allEntries.length === 0) return;
    const entries = filteredCategory
      ? allEntries.filter((e) => e.category === filteredCategory)
      : allEntries;

    const svgEl = svgRef.current;
    const width = svgEl.clientWidth || 800;
    const height = svgEl.clientHeight || 500;
    while (svgEl.firstChild) svgEl.removeChild(svgEl.firstChild);

    const { nodes, links } = buildGraph(entries);
    const svgNS = 'http://www.w3.org/2000/svg';
    const g = document.createElementNS(svgNS, 'g');
    svgEl.appendChild(g);

    links.forEach((link) => {
      const line = document.createElementNS(svgNS, 'line');
      line.setAttribute('stroke', '#333');
      line.setAttribute('stroke-width', String(Math.min(link.weight, 3)));
      line.setAttribute('opacity', '0.4');
      g.appendChild(line);
      link.el = line;
    });

    nodes.forEach((node) => {
      const r = nodeRadius(node);
      const circle = document.createElementNS(svgNS, 'circle');
      circle.setAttribute('r', String(r));
      circle.setAttribute('fill', getCategoryColor(node.category));
      circle.setAttribute('opacity', '0.85');
      circle.setAttribute('cursor', 'pointer');
      circle.addEventListener('mouseenter', (e) => {
        const rect = svgEl.getBoundingClientRect();
        setTooltip({ node, x: (e as MouseEvent).clientX - rect.left, y: (e as MouseEvent).clientY - rect.top });
      });
      circle.addEventListener('mouseleave', () => setTooltip(null));
      circle.addEventListener('click', () => navigate(`/insight/${node.id}`));
      g.appendChild(circle);
      node.el = circle;
    });

    const sim = d3Force.forceSimulation(nodes)
      .force('link', d3Force.forceLink<GraphNode, GraphLink>(links).id((d) => d.id).distance(80).strength(0.3))
      .force('charge', d3Force.forceManyBody().strength(-120))
      .force('center', d3Force.forceCenter(width / 2, height / 2))
      .force('collision', d3Force.forceCollide().radius((d) => nodeRadius(d as GraphNode) + 4));

    sim.on('tick', () => {
      links.forEach((link) => {
        if (!link.el) return;
        const s = link.source as GraphNode;
        const t = link.target as GraphNode;
        link.el.setAttribute('x1', String(s.x ?? 0));
        link.el.setAttribute('y1', String(s.y ?? 0));
        link.el.setAttribute('x2', String(t.x ?? 0));
        link.el.setAttribute('y2', String(t.y ?? 0));
      });
      nodes.forEach((node) => {
        if (node.el) {
          node.el.setAttribute('cx', String(node.x ?? 0));
          node.el.setAttribute('cy', String(node.y ?? 0));
        }
      });
    });

    return () => { sim.stop(); };
  }, [allEntries, filteredCategory, navigate]);

  const categories = [...new Set(allEntries.map((e) => e.category))].sort();

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-screen">
      <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilteredCategory(null)}
          className={filteredCategory === null ? 'badge' : 'btn-ghost text-xs px-2 py-1'}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilteredCategory(cat === filteredCategory ? null : cat)}
            className="btn-ghost text-xs px-2 py-1"
            style={filteredCategory === cat ? { background: getCategoryColor(cat), color: '#fff', borderColor: getCategoryColor(cat) } : {}}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="flex-1 relative overflow-hidden bg-gray-900">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
            Loading graph...
          </div>
        )}
        <svg ref={svgRef} className="w-full h-full" />
        {tooltip && (
          <div
            className="absolute card p-3 text-xs pointer-events-none z-20 max-w-xs"
            style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
          >
            <div className="font-semibold text-gray-100 mb-1">{tooltip.node.title}</div>
            <div className="text-gray-500 mb-2">{tooltip.node.category} · {tooltip.node.relevanceScore}/10</div>
            <div className="text-obsidian-accent text-xs">Click to open →</div>
          </div>
        )}
      </div>
    </div>
  );
}
